import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, BookOpen, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, aud } from "@/lib/api";
import { ARTICLES, IMAGES, ARTICLE_IMAGES } from "@/data/site";
import { AUDIENCE_PAGES } from "@/data/audiences";

const ToolkitRow = ({ product, index }) => {
  const [buying, setBuying] = useState(false);

  const buyNow = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: [product.id],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout could not be started. Please try again.");
      setBuying(false);
    }
  };

  return (
    <FadeIn delay={index * 0.05}>
      <div
        className="flex flex-col sm:flex-row gap-5 bg-white border border-cloud p-5 hover:border-teal transition-colors"
        data-testid={`audience-toolkit-${product.id}`}
      >
        <div className="sm:w-44 shrink-0">
          <div className="relative aspect-[4/3] overflow-hidden bg-sand-light">
            <img
              src={IMAGES[product.image]}
              alt={product.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <span className="absolute top-2 left-2 bg-ivory/95 text-teal text-[10px] font-mono uppercase tracking-[0.14em] px-2.5 py-1">
              {product.tag}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-serif text-xl text-teal">{product.title}</h3>
            <span className="font-serif text-xl text-teal">{aud(product.price)}</span>
          </div>
          <p className="mt-1.5 text-sm text-charcoal/70 leading-relaxed">{product.description}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {product.available ? (
              <button
                onClick={buyNow}
                disabled={buying}
                data-testid={`audience-buy-${product.id}`}
                className="inline-flex items-center gap-2 bg-teal text-ivory text-xs font-mono uppercase tracking-[0.14em] px-5 py-2.5 rounded-full hover:bg-teal-dark transition-colors disabled:opacity-60"
              >
                {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {buying ? "Starting checkout…" : "Buy now — instant PDF"}
              </button>
            ) : (
              <span
                data-testid={`audience-coming-soon-${product.id}`}
                className="inline-flex items-center gap-2 bg-sand-light text-charcoal/55 text-xs font-mono uppercase tracking-[0.14em] px-5 py-2.5 rounded-full"
              >
                Coming soon
              </span>
            )}
            <Link to="/toolkits" className="text-xs font-mono uppercase tracking-[0.12em] text-teal underline underline-offset-4">
              Bundle &amp; save in store
            </Link>
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

const AudiencePage = () => {
  const { slug } = useParams();
  const cfg = AUDIENCE_PAGES[slug];
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const toolkits = useMemo(
    () => (cfg ? cfg.toolkits.map((id) => products.find((p) => p.id === id)).filter(Boolean) : []),
    [cfg, products]
  );
  const guides = useMemo(
    () => (cfg ? cfg.guides.map((s) => ARTICLES.find((a) => a.slug === s)).filter(Boolean) : []),
    [cfg]
  );

  if (!cfg) {
    return (
      <div className="py-32 text-center" data-testid="audience-not-found">
        <h1 className="font-serif text-3xl text-teal">Page not found</h1>
        <Link to="/" className="mt-4 inline-block text-sm text-teal underline underline-offset-4">Back home</Link>
      </div>
    );
  }

  return (
    <div data-testid={`audience-page-${slug}`}>
      <Seo title={cfg.seo.title} description={cfg.seo.description} />

      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> All pathways
          </Link>
          <FadeIn>
            <Eyebrow>Start here</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-teal tracking-tight">{cfg.question}</h1>
            <p className="mt-2 font-serif text-xl sm:text-2xl text-charcoal/70">{cfg.title}</p>
            <p className="mt-5 text-charcoal/75 leading-relaxed">{cfg.intro}</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <Eyebrow>What we can offer you</Eyebrow>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-teal tracking-tight">
              Toolkits built for this
            </h2>
          </FadeIn>
          <div className="mt-8 space-y-4" data-testid="audience-toolkits">
            {toolkits.length === 0 && (
              <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 text-teal animate-spin" /></div>
            )}
            {toolkits.map((p, i) => (
              <ToolkitRow key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16 bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <Eyebrow>Free to read</Eyebrow>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-teal tracking-tight">
              Start with these guides
            </h2>
          </FadeIn>
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="audience-guides">
            {guides.map((a, i) => (
              <FadeIn key={a.slug} delay={i * 0.04}>
                <Link
                  to={`/hub/${a.slug}`}
                  data-testid={`audience-guide-${a.slug}`}
                  className="group flex gap-4 h-full bg-white border border-cloud p-5 hover:border-teal transition-colors"
                >
                  <div className="w-20 shrink-0 aspect-[4/3] overflow-hidden bg-sand-light">
                    <img
                      src={IMAGES[ARTICLE_IMAGES[a.slug]]}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal/50 flex items-center gap-1.5">
                      <BookOpen className="w-3 h-3" /> {a.category} · {a.minutes} min
                    </p>
                    <h3 className="mt-1.5 font-serif text-base text-teal leading-snug group-hover:underline underline-offset-4">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-teal text-ivory p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="audience-next">
              <div>
                <p className="font-serif text-xl">{cfg.beyond.title}</p>
                <p className="mt-1.5 text-sm text-ivory/75 leading-relaxed max-w-xl">{cfg.beyond.text}</p>
              </div>
              <Link
                to={cfg.beyond.to}
                data-testid="audience-next-link"
                className="inline-flex items-center gap-2 bg-ivory text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-sand-light transition-colors shrink-0"
              >
                {cfg.beyond.label} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Link to="/book" className="inline-flex items-center gap-2 text-sm text-teal font-medium hover:underline underline-offset-4">
                Prefer to talk it through? Book a planner <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default AudiencePage;
